import { useState, useEffect } from "react";
import RightTimeApi from "@/API"; // Ensure you import RightTimeApi if it's used
import Enums from "@/enums";
import { FaPlusSquare } from "react-icons/fa";
import { MdCheckBoxOutlineBlank, MdCheckBox, MdEdit, MdCameraswitch } from "react-icons/md";
import TestTypePicker from "../modals/TestTypePicker";
import { FaExclamationCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import ToolTip from "../ToolTip";
import { BsClipboardPlus } from "react-icons/bs";
import { IoCaretForwardCircleOutline } from "react-icons/io5";
import { LuClipboardCopy } from "react-icons/lu";
import RequestModal from "@modals/RequestModal";
import { GoProjectSymlink } from "react-icons/go";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import { codeTimeoutMinutesToHours } from "@/utils/testTimeoutDisplayHelpers";
import { useI18n } from "@/i18n/I18nProvider";
import { apiMessage } from "@/utils/apiMessage";

export default function TestList(props) {
  const [roles] = useState([]);
  const [showTestTypePicker, setShowTestTypePicker] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedCttId, setSelectedCttId] = useState(null);
  const [selectedCttName, setSelectedCttName] = useState("");
  const { t } = useI18n();

  function getEvalTypeName(value) {
    return Enums.getTestEvalComparisonLabel(value);
  }
  if (!props?.data?.testTypes) return null

  function onStartAddTestType() {
    setShowTestTypePicker(true);
  }

  async function onCloseTestTypePickerAsync(e) {
    if (!e) {
      setShowTestTypePicker(false);
      return;
    }

    if (e.mode === "copy") {
      let lastNewId: number | undefined;
      let copiedCount = 0;

      for (const sourceCttId of e.sourceCttIds) {
        const resp = await RightTimeApi.postAsync(
          `/CompanyTestType/${sourceCttId}/CopyToCompany`,
          { companyId: props.tabData.company.id }
        );
        if (resp?.ok) {
          copiedCount += 1;
          lastNewId = resp.jsonData?.id;
          continue;
        }

        const msg = apiMessage(resp);
        if (msg) toast.error(msg);
      }

      if (copiedCount > 0) {
        await props.updateData(lastNewId);
        toast.success(
          copiedCount === 1
            ? t("Test Type Copied")
            : `${copiedCount} test types copied`
        );
        setShowTestTypePicker(false);
      }
      return;
    }

    const newCTT = {
      testTypeId: e.id,
      companyId: props.tabData.company.id,
      name: e.name,
      fields: e.fields ?? [],
    };

    var resp = await RightTimeApi.postAsync("/CompanyTestType", newCTT);
    if (resp && resp.ok) {
      const newId = resp.jsonData?.id;
      await props.updateData(newId);
      toast.success(t("Test Type Added"));
      setShowTestTypePicker(false);
      return;
    }

    const msg = apiMessage(resp);
    if (msg) toast.error(msg);
  }

  async function toggleActive(e) {
    e.stopPropagation();
    const testTypeId = e.currentTarget.parentElement.dataset.id;
    const resp = await RightTimeApi.putAsync(`/CompanyTestType/${testTypeId}/ActiveToggle`);
    if (resp && resp.ok) {
      props.updateData();
      toast.success(t("Test Type Updated"));
    }
  }
  async function setRestartMode(cttId, mode) {
    const resp = await RightTimeApi.putAsync(`/CompanyTestType/${cttId}/RestartableToggle`, { restartable: mode });
    if (resp && resp.ok) {
      props.updateData();
      toast.success(t("Test Type Updated"));
    }
  }
  async function toggleShowInactive() {
    setShowInactive(!showInactive);
  }

  async function copyCttUrlToClipboard(e) {
    e.stopPropagation();

    const cttId = e.currentTarget.dataset.id;
    const cttIdInt = parseInt(cttId, 10);
    const testType = props?.data?.testTypes?.find(
      (tt) => tt.companyTestType?.id === cttIdInt
    );
    let url = '${import.meta.env.VITE_TEST_RUNNER_URL}';
    const code = testType?.companyTestType?.code;
    if (code) {
      url = `${import.meta.env.VITE_TEST_RUNNER_URL}${code}`;
      try {
        const copied = await copyUrlAsLink(url);
        if (copied) {
          toast.success(t("URL copied to clipboard"));
        } else {
          toast.error(t("Could not copy URL to clipboard"));
        }
      } catch (err) {
        const reason = String(err?.message || err || t("unknown error"));
        toast.error(`${t("Could not copy URL:")} ${reason}`);
      }
    } else {
      toast.error(t("No code available for this test type. Please create a code to get the URL."));
    }

  }

  const sortedTestTypes = [...props.data.testTypes].sort((a, b) =>
    a.companyTestType.name.localeCompare(b.companyTestType.name)
  );

  function isActiveRecord(ctt) {
    if (typeof ctt?.active === "boolean") return ctt.active;
    return !!ctt?.companyTestType?.active;
  }

  function cttHasProctorProvidedFields(ctt) {
    if (typeof ctt?.hasProctorProvidedFields === "boolean") {
      return ctt.hasProctorProvidedFields;
    }
    return ctt.fields?.some((field) => field.proctorProvided === true);
  }

  function cttCreateAction(ctt) {
    if (ctt.errors && ctt.errors.length > 0) {
      return (
        <ToolTip text={ctt.errors}>
          <FaExclamationCircle className="exclamationIcon" />
        </ToolTip>
      );
    }
    if (!cttHasProctorProvidedFields(ctt)) {
      return (
        <LuClipboardCopy
          className="pointer actionIcon copyTestUrlToClipboard"
          data-id={ctt.companyTestType.id}
          title="Copy URL to clipboard"
          data-help-id="CopyTestUrl"
          onClick={(e) => {
            e.stopPropagation();
            copyCttUrlToClipboard(e);
          }}
        />
      );
    }
    return (
      <GoProjectSymlink
        className="pointer actionIcon createTestIcon"
        data-id={ctt.companyTestType.id}
        title="Create Test"
        data-help-id="CreateTest"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedCttId(ctt.companyTestType.id);
          setSelectedCttName(ctt.companyTestType.name || "");
          setIsRequestModalOpen(true);
        }}
      />
    );
  }

  return (
    <>
      <div className="tableWrapper dimensionTable horizontal testTypesTable scrollable rowClickable">
        <table>
          <caption>
            <span className="title">Company Test Types</span>
            <span className="captionRight">
              <FaPlusSquare
                className="actionIcon plusIcon addCompanyTestType"
                data-help-id="addCompanyTestType"
                onClick={onStartAddTestType}
              />
            </span>
          </caption>
          <thead>
            <tr>
              <th>ID</th>
              <th onClick={toggleShowInactive} style={{ cursor: "pointer" }} data-help-id="ShowSelectedOrAll">
                <span>{showInactive ? t("All") : t("Active")}</span>
              </th>
              <th data-help-id="RestartableCtt">Restart</th>
              <th>Name</th>
              <th>Test Type</th>
              <th>Eval</th>
              <th>
                Code
                <br />
                Timeout
                <br />
                Hours
              </th>
              <th>Count</th>
              {roles &&
                roles?.map((r) => {
                  return <th key={"roles" + r.id}>{r.name}</th>;
                })}
              <th>Create</th>
            </tr>
          </thead>
          <tbody>

            {sortedTestTypes &&
              sortedTestTypes.length > 0 &&
              sortedTestTypes.map((ctt) => {
                if (!showInactive && !isActiveRecord(ctt)) return null;
                return (
                  <tr
                    key={"ctt" + ctt.companyTestType.id}
                    data-id={ctt.companyTestType.id}
                    data-test-type={ctt.companyTestType.testTypeName}
                    className={
                      props?.activeTestType?.companyTestType?.id ===
                        ctt.companyTestType.id
                        ? "active"
                        : ""
                    }
                    onClick={(e) => {
                      props.updateActiveTestTypeId(ctt.companyTestType.id);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to copy URL to clipboard"
                  >
                    <td>{ctt.companyTestType.id}</td>
                    <td onClick={(e) => { e.stopPropagation(); toggleActive(e); }} style={{ cursor: 'pointer' }}>
                      {isActiveRecord(ctt) ? (
                        <MdCheckBox className="actionIcon" />) : (
                        <MdCheckBoxOutlineBlank className="actionIcon" />)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} title={Enums.getTestRestartModeLabel(ctt.companyTestType.restartable)}>
                      <select
                        aria-label="Restart mode"
                        value={Number(ctt.companyTestType.restartable) || 0}
                        onChange={(e) => setRestartMode(ctt.companyTestType.id, Number(e.target.value))}
                      >
                        <option value={Enums.TEST_RESTART_MODE.NotRestartable}>Not restartable</option>
                        <option value={Enums.TEST_RESTART_MODE.SectionRestart}>Section restart</option>
                        <option value={Enums.TEST_RESTART_MODE.AnswerRestart}>Answer restart</option>
                      </select>
                    </td>
                    <td className="left">{ctt.companyTestType.name}</td>
                    <td className="left">{ctt.companyTestType.testTypeName}</td>
                    <td>{getEvalTypeName(ctt.companyTestType.evalComparison)}</td>
                    <td className="number">
                      {codeTimeoutMinutesToHours(ctt.companyTestType.codeTimeoutMinutes)}
                    </td>
                    <td className="number">
                      {props.countsLoaded === false ? "—" : (ctt.testCount ?? 0)}
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      {cttCreateAction(ctt)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {showTestTypePicker && (
        <>
          {/* {(()=>{debugger})()} */}

          <TestTypePicker
            isOpen={showTestTypePicker}
            onClose={onCloseTestTypePickerAsync}
            tabData={props.tabData}
          />
        </>
      )}
      <RequestModal
        isOpen={isRequestModalOpen}
        title="Create Test"
        onClose={() => {
          setIsRequestModalOpen(false);
          setSelectedCttId(null);
          setSelectedCttName("");
        }}
        companyId={props.tabData?.company?.id}
        company={props.tabData?.company}
        updateSpinner={props.tabData?.updateSpinner}
        onCreated={async () => {
          setIsRequestModalOpen(false);
          setSelectedCttId(null);
          setSelectedCttName("");
          props.updateData();
        }}
        adminPerms={props.tabData?.adminPerms}
        cttId={selectedCttId}
        cttName={selectedCttName}
        className="createTestRequestModal"
      />
    </>
  );
}

